import React, { useEffect, useRef, useState } from "react";
import Notification from "./Notification";
import QRCode from "react-qr-code";
import { useAuth } from "../contexts/AuthContext";
import { apiFetch } from "../lib/api";
import "./Dashboard.css";
import TripPathMap from "./TripPathMap";

// Date formatting helpers (shared approach with RiderDashboard)
const pad2 = (n: number) => n.toString().padStart(2, "0");
const toDate = (v: string | Date | null | undefined): Date | null => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d.getTime()) ? null : d;
};
const formatDate = (v: string | Date | null | undefined): string => {
  const d = toDate(v);
  if (!d) return "-";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};
const formatDateTime = (v: string | Date | null | undefined): string => {
  const d = toDate(v);
  if (!d) return "-";
  return `${formatDate(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

interface Trip {
  id: string;
  rideCode: string;
  startTime: string;
  startLocation: string;
  startLocationName?: string | null;
  endTime?: string;
  endLocation?: string;
  endLocationName?: string | null;
  state: string;
  userTrips: any[];
  locationLogs: any[];
}

const DriverDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [notice, setNotice] = useState<{ kind: 'success' | 'error' | 'info'; message: string } | null>(null);
  const showNotice = (message: string, kind: 'success' | 'error' | 'info' = 'info') => setNotice({ kind, message });
  const [isOnline, setIsOnline] = useState<boolean>(false);
  const [currentTrip, setCurrentTrip] = useState<Trip | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  // Start location is determined via GPS on startTrip; no manual entry thereafter
  // Interval (ms) for sending location updates. Change here to adjust globally.
  const LOCATION_POLL_INTERVAL = 10000; // 10 seconds
  const locationIntervalRef = useRef<number | null>(null);
  const [showProfile, setShowProfile] = useState<boolean>(false);
  const [showActivity, setShowActivity] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [confirmEndTripOpen, setConfirmEndTripOpen] = useState<boolean>(false);
  const [confirmEndRiderUserId, setConfirmEndRiderUserId] = useState<string | null>(null);

  // Driver reviews panel state (for reviewing riders)
  const [reviewPanelOpen, setReviewPanelOpen] = useState<boolean>(false);
  const [reviewTargets, setReviewTargets] = useState<Array<{ userId: string; name: string; tripId: string }>>([]);
  const [reviewDrafts, setReviewDrafts] = useState<Record<string, { rating: number; comment: string; submitting: boolean; submitted: boolean; error?: string }>>({});

  const openReviewPanelForRiders = (riders: Array<{ id: string; name: string }>, tripId: string) => {
    const targets = riders.map((r) => ({ userId: r.id, name: r.name, tripId }));
    setReviewTargets(targets);
    const drafts: Record<string, { rating: number; comment: string; submitting: boolean; submitted: boolean; error?: string }> = {};
    for (const t of targets) drafts[t.userId] = { rating: 0, comment: "", submitting: false, submitted: false };
    setReviewDrafts(drafts);
    setReviewPanelOpen(true);
  };

  const submitDriverReview = async (targetUserId: string) => {
    const draft = reviewDrafts[targetUserId];
    const target = reviewTargets.find((t) => t.userId === targetUserId);
    if (!draft || !target) return;
    if (draft.rating < 1 || draft.rating > 5) {
      setReviewDrafts((prev) => ({ ...prev, [targetUserId]: { ...prev[targetUserId], error: "Please select 1-5 stars." } }));
      return;
    }
    setReviewDrafts((prev) => ({ ...prev, [targetUserId]: { ...prev[targetUserId], submitting: true, error: undefined } }));
    try {
      const res = await apiFetch("/reviews", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          aboutId: target.userId,
          rating: draft.rating,
          comment: draft.comment.trim() || undefined,
          tripId: target.tripId,
        }),
      });
      const data = await res.json();
      if (data?.success) {
        setReviewDrafts((prev) => ({ ...prev, [targetUserId]: { ...prev[targetUserId], submitting: false, submitted: true } }));
        // Optionally hide the card after submission
        setReviewTargets((prev) => prev.filter((t) => t.userId !== targetUserId));
      } else {
        setReviewDrafts((prev) => ({ ...prev, [targetUserId]: { ...prev[targetUserId], submitting: false, error: data?.error || "Failed to submit review." } }));
      }
    } catch (e) {
      setReviewDrafts((prev) => ({ ...prev, [targetUserId]: { ...prev[targetUserId], submitting: false, error: "Network error." } }));
    }
  };

  const skipDriverReview = (targetUserId: string) => {
    // Simply remove this target from the panel
    setReviewTargets((prev) => prev.filter((t) => t.userId !== targetUserId));
    setReviewDrafts((prev) => {
      const n = { ...prev };
      delete n[targetUserId];
      return n;
    });
  };

  // Activity (history) state for driver
  interface HistoryTrip {
    id: string;
    serviceDate: string;
    startTime: string;
    startLocation: string;
    startLocationName?: string | null;
    endTime?: string | null;
    endLocation?: string | null;
    endLocationName?: string | null;
    state: string;
  }
  const [tripHistory, setTripHistory] = useState<HistoryTrip[]>([]);
  const [selectedHistoryTrip, setSelectedHistoryTrip] = useState<HistoryTrip | null>(null);
  const [historyTripLogs, setHistoryTripLogs] = useState<Array<{ location: string; timestamp: string }>>([]);
  const [historyMetrics, setHistoryMetrics] = useState<{ distanceKm: number; durationMin: number } | null>(null);
  const [historyTripRiderCount, setHistoryTripRiderCount] = useState<number | null>(null);

  const parseLocation = (loc?: string | null): { lat: number; lng: number } | null => {
    if (!loc) return null;
    const parts = loc.split(",").map((s) => parseFloat(s.trim()));
    if (parts.length !== 2 || parts.some((x) => Number.isNaN(x))) return null;
    return { lat: parts[0], lng: parts[1] };
  };

  // Fetch driver's activity history when Activity view is opened
  useEffect(() => {
    const fetchHistory = async () => {
      if (!showActivity || !user?.id) return;
      try {
        const res = await apiFetch(`/trips/driver/${user.id}/history`);
        const data = await res.json();
        if (data?.success) {
          setTripHistory(data.trips || []);
        }
      } catch (e) {
        // ignore
      }
    };
    fetchHistory();
  }, [showActivity, user?.id]);

  // Auto-close review panel if all targets handled
  useEffect(() => {
    if (reviewPanelOpen && reviewTargets.length === 0) {
      setReviewPanelOpen(false);
    }
  }, [reviewPanelOpen, reviewTargets.length]);

  // When a history trip is selected, fetch its logs and compute metrics
  useEffect(() => {
    const toCoords = (loc: string | null | undefined) => {
      const p = parseLocation(loc ?? undefined);
      return p ? [p.lat, p.lng] as [number, number] : null;
    };
    const haversineKm = (a: [number, number], b: [number, number]) => {
      const R = 6371; // km
      const toRad = (d: number) => (d * Math.PI) / 180;
      const dLat = toRad(b[0] - a[0]);
      const dLon = toRad(b[1] - a[1]);
      const lat1 = toRad(a[0]);
      const lat2 = toRad(b[0]);
      const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
      return 2 * R * Math.asin(Math.sqrt(h));
    };

    const fetchLogs = async () => {
      if (!selectedHistoryTrip) {
        setHistoryTripLogs([]);
        setHistoryMetrics(null);
        setHistoryTripRiderCount(null);
        return;
      }
      try {
        const res = await apiFetch(`/trips/${selectedHistoryTrip.id}/location-logs`);
        const data = await res.json();
        if (data?.success) {
          const logs = data.logs as Array<{ location: string; timestamp: string }>;
          setHistoryTripLogs(logs);
          // Compute distance and duration
          let distance = 0;
          const coords: [number, number][] = [];
          for (const l of logs) {
            const c = toCoords(l.location);
            if (c) coords.push(c);
          }
          for (let i = 1; i < coords.length; i++) {
            distance += haversineKm(coords[i - 1], coords[i]);
          }
          const startTs = logs[0]?.timestamp || selectedHistoryTrip.startTime;
          const endTs = logs[logs.length - 1]?.timestamp || selectedHistoryTrip.endTime || selectedHistoryTrip.startTime;
          const durationMin = Math.max(0, Math.round((new Date(endTs).getTime() - new Date(startTs).getTime()) / 60000));
          setHistoryMetrics({ distanceKm: Math.round(distance * 10) / 10, durationMin });
          // Also fetch trip details to get rider count
          try {
            const detRes = await apiFetch(`/trips/${selectedHistoryTrip.id}`);
            const det = await detRes.json();
            if (det?.success && Array.isArray(det.trip?.userTrips)) {
              setHistoryTripRiderCount(det.trip.userTrips.length);
            } else {
              setHistoryTripRiderCount(null);
            }
          } catch {
            setHistoryTripRiderCount(null);
          }
        } else {
          setHistoryTripLogs([]);
          setHistoryMetrics(null);
          setHistoryTripRiderCount(null);
        }
      } catch (e) {
        setHistoryTripLogs([]);
        setHistoryMetrics(null);
        setHistoryTripRiderCount(null);
      }
    };
    fetchLogs();
  }, [selectedHistoryTrip]);

  // API calls
  const goLive = async () => {
    // console.log("Go Live clicked, user:", user);
    if (!user?.id) {
      console.error("No user ID found:", user);
      showNotice("No user ID found. Please login again.", 'error');
      return;
    }

    setIsLoading(true);
    try {
      // console.log("Sending request to go-live with driverId:", user.id);
      const response = await apiFetch("/trips/go-live", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ driverId: user.id }),
      });

      // console.log("Response status:", response.status);
      const result = await response.json();
      // console.log("Response result:", result);

      if (result.success) {
        setIsOnline(true);
        showNotice("You are now live and available for trips!", 'success');
      } else {
        showNotice(`Error: ${result.error}`, 'error');
      }
    } catch (error) {
      console.error("Go live error:", error);
      showNotice("Failed to go live. Please try again.", 'error');
    }
    setIsLoading(false);
  };

  const goOffline = async () => {
    // console.log("Go Offline clicked, user:", user);
    if (!user?.id) {
      console.error("No user ID found:", user);
      showNotice("No user ID found. Please login again.", 'error');
      return;
    }

    setIsLoading(true);
    try {
      // console.log("Sending request to go-offline with driverId:", user.id);
      const response = await apiFetch("/trips/go-offline", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ driverId: user.id }),
      });

      // console.log("Response status:", response.status);
      const result = await response.json();
      // console.log("Response result:", result);

      if (result.success) {
        setIsOnline(false);
        setCurrentTrip(null);
        showNotice("You are now offline.", 'info');
      } else {
        showNotice(`Error: ${result.error}`, 'error');
      }
    } catch (error) {
      console.error("Go offline error:", error);
      showNotice("Failed to go offline. Please try again.", 'error');
    }
    setIsLoading(false);
  };

  const getCurrentLocation = (): Promise<string | null> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        console.error("Geolocation is not supported by this browser.");
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          resolve(`${latitude}, ${longitude}`);
        },
        (error) => {
          // On error just resolve null (no manual prompt)
          console.error("Error getting location (will use null):", error);
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 60000,
          maximumAge: 0,
        }
      );
    });
  };

  const startTrip = async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      // Get current GPS location
      const currentLocation = await getCurrentLocation();
      console.log("Obtained current location:", currentLocation);

      const response = await apiFetch("/trips/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          driverId: user.id,
          startLocation: currentLocation,
        }),
      });

      const result = await response.json();
      if (result.success) {
        setCurrentTrip(result.trip);
        // Start periodic location logging
        try {
          // Immediately send current location (already obtained as currentLocation)
          if (currentLocation) {
            await apiFetch("/trips/location", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                tripId: result.trip.id,
                location: currentLocation,
              }),
            });
          } else {
            console.warn("Initial location unavailable (null); not sending log yet.");
          }
        } catch (err) {
          console.warn("Failed to send initial location log:", err);
        }

        // Start polling
        if (locationIntervalRef.current == null) {
          const id = window.setInterval(async () => {
            try {
              const loc = await getCurrentLocation();
              if (loc) {
                console.log("Periodic location update:", loc);
                await apiFetch("/trips/location", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ tripId: result.trip.id, location: loc }),
                });
              } else {
                console.warn("Periodic location update skipped (GPS unavailable). Will retry.");
              }
            } catch (err) {
              console.warn("Periodic location update failed:", err);
            }
          }, LOCATION_POLL_INTERVAL);
          locationIntervalRef.current = id;
        }
        showNotice(`Trip started! Ride Code: ${result.rideCode}`, 'success');
      } else {
        showNotice(`Error: ${result.error}`,'error');
      }
    } catch (error) {
      showNotice(
        `Failed to start trip: ${
          error instanceof Error ? error.message : "Please try again."
        }`, 'error'
      );
    }
    setIsLoading(false);
  };

  const endTrip = async () => {
    if (!currentTrip?.id) {
      showNotice("Trip not found or not ongoing.", 'error');
      return;
    }

    // Snapshot riders for post-trip review panel before we clear currentTrip
    const ridersSnapshot: Array<{ id: string; name: string }> = (currentTrip.userTrips || [])
      .map((ut: any) => ut?.user)
      .filter((u: any) => u && typeof u.id === "string" && typeof u.name === "string")
      .map((u: any) => ({ id: u.id, name: u.name }));

    setIsLoading(true);
    try {
      // Send a final location log before ending the trip
      try {
        const finalLoc = await getCurrentLocation();
        console.log("Final location before ending trip:", finalLoc);

        if (finalLoc) {
          await apiFetch("/trips/location", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ tripId: currentTrip.id, location: finalLoc }),
          });
        } else {
          console.warn("Final GPS location unavailable; skipping final location log.");
        }
      } catch (err) {
        console.warn("Failed to log final location before ending trip:", err);
      }

      const response = await apiFetch("/trips/end", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tripId: currentTrip.id,
          endLocation: null,
        }),
      });

      const result = await response.json();
      if (result.success) {
        // Open vertical reviews panel for all riders of this trip
        openReviewPanelForRiders(ridersSnapshot, currentTrip.id);
        setCurrentTrip(null);
        // Stop periodic location logging
        if (locationIntervalRef.current != null) {
          clearInterval(locationIntervalRef.current);
          locationIntervalRef.current = null;
        }
        showNotice("Trip completed successfully!", 'success');
      } else {
        showNotice(`Error: ${result.error}`, 'error');
      }
    } catch (error) {
      showNotice("Failed to end trip. Please try again.", 'error');
    }
    setIsLoading(false);
  };

  // No manual updateLocation function needed; periodic logging handles updates

  const endRiderTrip = async (userId: string, riderName: string) => {
    if (!currentTrip?.id) return;

    setIsLoading(true);
    try {
      // Ask server to derive last known location for alighting
      const response = await apiFetch("/trips/end-rider", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tripId: currentTrip.id,
          userId,
          alightingLocation: null,
        }),
      });

      const result = await response.json();
      if (result.success) {
        showNotice(`${riderName} has successfully alighted from the trip!`, 'success');
        // Refresh the trip data to update the rider list
        const tripResponse = await apiFetch(
          `/trips/driver/${user?.id}/current`
        );
        const tripResult = await tripResponse.json();
        if (tripResult.success && tripResult.trip) {
          setCurrentTrip(tripResult.trip);
        }
      } else {
        showNotice(`Error: ${result.error}`, 'error');
      }
    } catch (error) {
      showNotice("Failed to end rider trip. Please try again.", 'error');
    }
    setIsLoading(false);
  };

  // Poll for trip updates every 5 seconds
  useEffect(() => {
    if (!user?.id) return;

    const interval = setInterval(async () => {
      try {
        const response = await apiFetch(`/trips/driver/${user.id}/current`);
        const result = await response.json();
        if (result.success && result.trip) setCurrentTrip(result.trip);
      } catch (error) {
        console.error("Failed to refresh trip data:", error);
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(interval);
  }, [user]);

  // Get driver status and current trip on component mount
  useEffect(() => {
    const initializeDriverStatus = async () => {
      if (!user?.id) {
        return;
      }

      try {
        // First check driver's online status
        const statusResponse = await apiFetch(
          `/trips/driver/${user.id}/status`
        );
        const statusResult = await statusResponse.json();

        if (statusResult.success) {
          setIsOnline(statusResult.isOnline);
        } else {
          // If status check fails, default to offline
          setIsOnline(false);
        }

        // Then check for current trip
        const tripResponse = await apiFetch(`/trips/driver/${user.id}/current`);
        const tripResult = await tripResponse.json();

        if (tripResult.success && tripResult.trip) {
          setCurrentTrip(tripResult.trip);
          setIsOnline(true); // If there's a trip, driver is definitely online
        } else {
          // Ensure we maintain the status from the status check
          if (statusResult.success) {
            setIsOnline(statusResult.isOnline);
          }
        }
      } catch (error) {
        console.error("Failed to initialize driver status:", error);
        // On error, default to offline
        setIsOnline(false);
      } finally {
        // Always set initialization as complete
        setIsInitializing(false);
      }
    };

    initializeDriverStatus();
  }, [user]);

  return (
    <div className="dashboard">
      {notice && (
        <Notification
          kind={notice.kind}
          message={notice.message}
          onClose={() => setNotice(null)}
        />
      )}
      {/* Mobile Menu Bar */}
      <div className="menu-bar">
        <button 
          className="menu-toggle-btn" 
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className="menu-icon">☰</span>
        </button>
        <div className="menu-title">
          <h1>Cab Service</h1>
          <p>Driver Dashboard</p>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div className="menu-overlay" onClick={() => setIsMenuOpen(false)}>
          <div className="menu-content" onClick={(e) => e.stopPropagation()}>
            <div className="menu-header">
              <h2>Menu</h2>
              <button 
                className="menu-close-btn" 
                onClick={() => setIsMenuOpen(false)}
                aria-label="Close menu"
              >
                ✕
              </button>
            </div>
            <div className="menu-items">
              <button 
                className="menu-item" 
                onClick={() => {
                  setShowActivity(true);
                  setShowProfile(false);
                  setIsMenuOpen(false);
                }}
              >
                <span className="menu-item-icon">🧭</span>
                <span>Activity</span>
              </button>
              <button 
                className="menu-item" 
                onClick={() => {
                  setShowProfile(true);
                  setShowActivity(false);
                  setIsMenuOpen(false);
                }}
              >
                <span className="menu-item-icon">👤</span>
                <span>View Profile</span>
              </button>
              <button 
                className="menu-item logout-item" 
                onClick={() => {
                  logout();
                  setIsMenuOpen(false);
                }}
              >
                <span className="menu-item-icon">🚪</span>
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dashboard Header */}
      <div className="dashboard-header">
        <div className="user-info">
          <h1>Welcome, {user?.name}!</h1>
          <p>Driver Dashboard</p>
        </div>
      </div>

      <div className="dashboard-content">
        {showActivity ? (
          <div className="profile-page">
            <div className="profile-page-header">
              <button
                className="back-btn"
                onClick={() => setShowActivity(false)}
                aria-label="Back to dashboard"
              >
                ← Back
              </button>
              <h2>Trips History</h2>
            </div>
            <div className="profile-page-content">
              {tripHistory.length === 0 ? (
                <p className="no-trips-message">No past trips yet.</p>
              ) : (
                <div className="trips-list">
                  {tripHistory.map((t) => (
                    <div
                      key={t.id}
                      className="trip-card"
                      onClick={() => setSelectedHistoryTrip(t)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="trip-header">
                        <div className="trip-info">
                          <div className="driver-name">
                            {(t.startLocationName || t.startLocation || 'Start')} → {(t.endLocationName || t.endLocation || 'End')}
                          </div>
                          <div className="ride-code">{formatDateTime(t.startTime)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
        <div className="status-section">
          <h2>Driver Status</h2>
          <div className="status-controls">
            <div className="status-controls-row">
              <div
                className={`status-indicator ${
                  isOnline ? "online" : "offline"
                }`}
              >
                {isOnline ? "Online" : "Offline"}
              </div>
              <button
                className={`status-btn ${isOnline ? "online" : "offline"}`}
                onClick={isOnline ? goOffline : goLive}
                disabled={isLoading || isInitializing}
              >
                {isInitializing
                  ? "Loading..."
                  : isLoading
                  ? isOnline
                    ? "Going Offline..."
                    : "Going Live..."
                  : isOnline
                  ? "Go Offline"
                  : "Go Live"}
              </button>
            </div>
            <p className="status-text">
              {isInitializing
                ? "Loading driver status..."
                : isOnline
                ? "You are live and available for trips"
                : "Go live to start receiving trip requests"}
            </p>
          </div>
        </div>

        {isOnline && !currentTrip && (
          <div className="status-section">
            <h2>Start New Trip</h2>
            <p className="section-description">
              Your current location will be automatically detected using GPS
            </p>
            <button
              className="primary-btn"
              onClick={startTrip}
              disabled={isLoading}
            >
              {isLoading ? "Getting Location..." : "Start Trip"}
            </button>
          </div>
        )}


        {currentTrip && (
          <div className="status-section">
            <h2 className="trip-section-title">Current Trip</h2>

            <div className="trip-overview">
              <div className="ride-code-card">
                <h3>Ride Code</h3>
                <div className="ride-code-display">
                  {currentTrip.rideCode}
                </div>
                <p className="ride-code-hint">
                  Share this code or QR with riders
                </p>

                {/* QR Code display for riders to scan */}
                <div className="qr-code-container">
                  <QRCode
                    value={currentTrip.rideCode}
                    size={160}
                    bgColor="#ffffff"
                    fgColor="#0f172a"
                    style={{ height: "160px", width: "160px" }}
                  />
                </div>
                <p className="qr-code-tip">
                  Tip: Ask riders to scan this QR
                </p>
              </div>

              <div className="trip-details-card">
                <h3>Trip Details</h3>
                <div className="trip-details-grid">
                  <div className="trip-detail-row">
                    <span className="detail-label">Location:</span>
                    <span className="detail-value">{currentTrip.startLocationName || currentTrip.startLocation}</span>
                  </div>
                  <div className="trip-detail-row">
                    <span className="detail-label">Started:</span>
                    <span className="detail-value">
                      {formatDateTime(currentTrip.startTime)}
                    </span>
                  </div>
                  <div className="trip-detail-row">
                    <span className="detail-label">Status:</span>
                    <span className="detail-value status-active">{currentTrip.state}</span>
                  </div>
                  <div className="trip-detail-row">
                    <span className="detail-label">Passengers:</span>
                    <span className="detail-value passenger-count">
                      {currentTrip.userTrips?.length || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div
              style={{
                display: 'block',
                marginTop: '20px'
              }}
            >
              <div className="input-group" style={{ width: '100%' }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "var(--text-primary)",
                    fontSize: "14px",
                  }}
                >
                  End Trip
                </label>
                {!confirmEndTripOpen && (
                  <button
                    onClick={() => setConfirmEndTripOpen(true)}
                    disabled={isLoading}
                    style={{
                      marginTop: "10px",
                      width: "100%",
                      backgroundColor: isLoading ? "var(--gray-400)" : "var(--error)",
                      color: "white",
                      border: "none",
                      padding: "15px 30px",
                      borderRadius: "var(--radius-md)",
                      fontSize: "16px",
                      fontWeight: "600",
                      cursor: isLoading ? "not-allowed" : "pointer",
                      opacity: isLoading ? 0.6 : 1,
                      boxShadow: "var(--shadow-sm)",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {isLoading ? "Ending..." : "End Trip"}
                  </button>
                )}
                {confirmEndTripOpen && (
                  <div style={{
                    marginTop: 10,
                    display: 'grid',
                    gap: 8,
                    background: 'var(--bg-tertiary)',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    borderRadius: 'var(--radius-md)'
                  }}>
                    <div style={{fontSize: 14,color: 'var(--text-secondary)'}}>
                      End trip now? This will finalize rider journeys and stop location updates.
                    </div>
                    <div style={{display: 'flex', gap: 8}}>
                      <button
                        className="secondary-btn"
                        onClick={() => setConfirmEndTripOpen(false)}
                        disabled={isLoading}
                        style={{flex:1}}
                      >
                        Cancel
                      </button>
                      <button
                        className="danger-btn"
                        onClick={() => { setConfirmEndTripOpen(false); endTrip(); }}
                        disabled={isLoading}
                        style={{flex:1}}
                      >
                        {isLoading ? 'Ending…' : 'Confirm End'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {currentTrip.userTrips && currentTrip.userTrips.length > 0 && (
              <div
                style={{
                  backgroundColor: "var(--bg-tertiary)",
                  padding: "var(--spacing-lg)",
                  borderRadius: "var(--radius-lg)",
                  border: "1px solid var(--border-light)",
                  marginTop: "25px",
                }}
              >
                <h3
                  style={{
                    color: "var(--text-primary)",
                    marginBottom: "20px",
                    fontSize: "18px",
                    fontWeight: "600",
                  }}
                >
                  Passengers{" "}
                  <span
                    style={{
                      color: "var(--text-secondary)",
                      fontSize: "15px",
                      fontWeight: "500",
                    }}
                  >
                    (
                    {
                      currentTrip.userTrips.filter(
                        (ut: any) => ut.status === "joined"
                      ).length
                    }{" "}
                    active / {currentTrip.userTrips.length} total)
                  </span>
                </h3>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  {currentTrip.userTrips.map((userTrip: any, index: number) => {
                    // console.log("Rendering userTrip:", userTrip);
                    return (
                        <div key={userTrip.id} className="passenger-card">
                          <div className="passenger-header">
                            <div className="passenger-avatar">
                              {userTrip.user.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="passenger-info">
                              <div className="passenger-name-row">
                                <span className="passenger-name">{userTrip.user.name}</span>
                                <span className={`passenger-status ${userTrip.status === "joined" ? "active" : "completed"}`}>
                                  {userTrip.status === "joined" ? "Active" : "Completed"}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="passenger-details">
                            <div className="passenger-detail-row">
                              <span className="detail-label">Joined:</span>
                              <span className="detail-value">
                                {formatDate(userTrip.boardingTime)} {new Date(userTrip.boardingTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </span>
                            </div>
                            {userTrip.alightingTime && (
                              <div className="passenger-detail-row">
                                <span className="detail-label">Alighted:</span>
                                <span className="detail-value">
                                  {formatDate(userTrip.alightingTime)} {new Date(userTrip.alightingTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="passenger-actions">
                            {userTrip.status === "joined" ? (
                              confirmEndRiderUserId === userTrip.user.id ? (
                                <div style={{
                                  display: 'grid',
                                  gap: 8,
                                  background: 'var(--bg-tertiary)',
                                  padding: '12px',
                                  border: '1px solid var(--border-light)',
                                  borderRadius: 'var(--radius-md)',
                                  width: '100%',
                                  boxSizing: 'border-box'
                                }}>
                                  <div style={{fontSize: 14, color: 'var(--text-secondary)'}}>
                                    End {userTrip.user.name}'s ride? This will finalize their journey.
                                  </div>
                                  <div style={{display: 'flex', gap: 8}}>
                                    <button
                                      className="secondary-btn"
                                      onClick={() => setConfirmEndRiderUserId(null)}
                                      disabled={isLoading}
                                      style={{flex: 1}}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      className="danger-btn"
                                      onClick={() => { setConfirmEndRiderUserId(null); endRiderTrip(userTrip.user.id, userTrip.user.name); }}
                                      disabled={isLoading}
                                      style={{flex: 1}}
                                    >
                                      {isLoading ? 'Ending…' : 'Confirm End'}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  className="end-ride-btn"
                                  onClick={() => setConfirmEndRiderUserId(userTrip.user.id)}
                                  disabled={isLoading}
                                  style={{width: '100%'}}
                                >
                                  {isLoading ? "Processing..." : "End Ride"}
                                </button>
                              )
                            ) : (
                              <div className="completed-badge">
                                Completed
                              </div>
                            )}
                          </div>
                        </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {showProfile && (
          <div className="profile-page">
            <div className="profile-page-header">
              <button 
                className="back-btn" 
                onClick={() => setShowProfile(false)}
                aria-label="Back to dashboard"
              >
                ← Back
              </button>
              <h2>Profile Information</h2>
            </div>
            
            <div className="profile-page-content">
              <div className="profile-card-large">
                <div className="profile-header-large">
                  <div className="profile-avatar-large">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="profile-info-large">
                    <div className="profile-name-large">{user?.name}</div>
                    <div className="profile-email-large">{user?.email}</div>
                    <div className="profile-role-large">
                      <span className="role-badge-large">{user?.role}</span>
                    </div>
                  </div>
                </div>
                
                <div className="profile-details-large">
                  <div className="profile-section">
                    <h3>Contact Information</h3>
                    <div className="profile-detail-large">
                      <div className="detail-label">Email Address</div>
                      <div className="detail-value">{user?.email}</div>
                    </div>
                  </div>
                  
                  <div className="profile-section">
                    <h3>Account Information</h3>
                    <div className="profile-detail-large">
                      <div className="detail-label">User Role</div>
                      <div className="detail-value">
                        <span className="role-badge" style={{ textTransform: 'uppercase' }}>{user?.role}</span>
                      </div>
                    </div>
                    <div className="profile-detail-large">
                      <div className="detail-label">Account Status</div>
                      <div className="detail-value">
                        <span className={`status-badge ${(user?.status === 'Inactive') ? 'inactive' : 'active'}`}>
                          {(user?.status === 'Inactive') ? 'Inactive' : 'Active'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {user?.vehicleInfo && (
                    <div className="profile-section">
                      <h3>Vehicle Information</h3>
                      <div className="profile-detail-large">
                        <div className="detail-label">Make</div>
                        <div className="detail-value">{user.vehicleInfo.make}</div>
                      </div>
                      <div className="profile-detail-large">
                        <div className="detail-label">Model</div>
                        <div className="detail-value">{user.vehicleInfo.model}</div>
                      </div>
                      <div className="profile-detail-large">
                        <div className="detail-label">Year</div>
                        <div className="detail-value">{user.vehicleInfo.year}</div>
                      </div>
                      <div className="profile-detail-large">
                        <div className="detail-label">License Plate</div>
                        <div className="detail-value">{user.vehicleInfo.licensePlate}</div>
                      </div>
                      <div className="profile-detail-large">
                        <div className="detail-label">Capacity</div>
                        <div className="detail-value">{user.vehicleInfo.capacity}</div>
                      </div>
                      <div className="profile-detail-large">
                        <div className="detail-label">Color</div>
                        <div className="detail-value">{user.vehicleInfo.color}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        </>
        )}

        {/* History Trip Details Modal */}
        {selectedHistoryTrip && (
          <div className="trip-details-modal">
            <div className="trip-details-modal-content">
              <div className="trip-details-modal-header">
                <h2>Trip Details</h2>
                <button 
                  className="close-modal-btn" 
                  onClick={() => setSelectedHistoryTrip(null)}
                  aria-label="Close trip details"
                >
                  ✕
                </button>
              </div>
              <div className="trip-details-modal-body">
                <div className="trip-overview" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
                  <div className="trip-details-card" style={{ gridColumn: '1 / -1' }}>
                    <h3>Route</h3>
                    <div className="card-body" style={{ padding: 0 }}>
                      <TripPathMap
                        coords={historyTripLogs
                          .map(l => parseLocation(l.location))
                          .filter((c): c is {lat:number; lng:number} => !!c)
                          .map(c => [c.lat, c.lng])}
                        height={300}
                      />
                    </div>
                  </div>

                  <div className="user-trip-status" style={{ marginTop: 12 }}>
                    <h3>Summary</h3>
                    <div className="status-details">
                      <div className="status-item"><strong>Date:</strong> <span>{formatDate(selectedHistoryTrip.serviceDate || selectedHistoryTrip.startTime)}</span></div>
                      <div className="status-item"><strong>Start:</strong> <span>{selectedHistoryTrip.startLocationName || selectedHistoryTrip.startLocation || '-'}</span></div>
                      <div className="status-item"><strong>End:</strong> <span>{selectedHistoryTrip.endLocationName || selectedHistoryTrip.endLocation || '-'}</span></div>
                      {historyMetrics && (
                        <>
                          <div className="status-item"><strong>Distance:</strong> <span>{historyMetrics.distanceKm} km</span></div>
                        </>
                      )}
                      <div className="status-item"><strong>Pickup Time:</strong> <span>{formatDateTime(selectedHistoryTrip.startTime)}</span></div>
                      {selectedHistoryTrip.endTime && (
                        <div className="status-item"><strong>Drop Time:</strong> <span>{formatDateTime(selectedHistoryTrip.endTime)}</span></div>
                      )}
                      {historyMetrics && (
                        <>
                          <div className="status-item"><strong>Duration:</strong> <span>{historyMetrics.durationMin} min</span></div>
                        </>
                      )}
                      {historyTripRiderCount != null && (
                        <div className="status-item"><strong>No. of Riders:</strong> <span>{historyTripRiderCount}</span></div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="trip-details-modal-footer">
                <button 
                  className="primary-btn" 
                  onClick={() => setSelectedHistoryTrip(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Driver Reviews Modal (centered, same style as rider) */}
        {reviewPanelOpen && reviewTargets.length > 0 && (
          <div
            role="dialog"
            aria-modal="true"
            className="trip-details-modal"
            style={{ zIndex: 1100 }}
          >
            <div className="trip-details-modal-content">
              <div className="trip-details-modal-header">
                <h2>Rate your riders</h2>
                <button
                  className="close-modal-btn"
                  onClick={() => {
                    setReviewPanelOpen(false);
                    setReviewTargets([]);
                    setReviewDrafts({});
                  }}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
              <div className="trip-details-modal-body">
                <div style={{ display: 'grid', gap: 16 }}>
                  {reviewTargets.map((t) => {
                    const d = reviewDrafts[t.userId] || { rating: 0, comment: "", submitting: false, submitted: false };
                    const setRating = (s: number) =>
                      setReviewDrafts((prev) => ({ ...prev, [t.userId]: { ...prev[t.userId], rating: s, error: undefined } }));
                    const setComment = (val: string) =>
                      setReviewDrafts((prev) => ({ ...prev, [t.userId]: { ...prev[t.userId], comment: val } }));
                    return (
                      <div key={t.userId} className="user-trip-status" style={{ padding: 12, border: '1px solid #1f2937', borderRadius: 12 }}>
                        <div style={{ fontSize: 14, color: '#94a3b8', marginBottom: 6 }}>
                          How was your ride with <strong style={{ color: 'var(--primary-teal)' }}>{t.name}</strong>?
                        </div>
                        {/* Stars */}
                        <div style={{ display: 'flex', gap: 8, fontSize: 28 }}>
                          {[1,2,3,4,5].map((s) => (
                            <button
                              key={s}
                              onClick={() => setRating(s)}
                              aria-label={`${s} star${s>1?'s':''}`}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: 28,
                                padding: 6,
                                color: s <= d.rating ? '#fbbf24' : '#475569',
                              }}
                            >
                              ★
                            </button>
                          ))}
                        </div>
                        {/* Comment */}
                        <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
                          <label style={{ color: '#cbd5e1', fontSize: 14 }}>Comments (optional)</label>
                          <textarea
                            value={d.comment}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val.length <= 500) setComment(val);
                            }}
                            placeholder="Share feedback to help improve rides (max 500 chars)"
                            style={{
                              background: '#0b1220',
                              color: '#e2e8f0',
                              border: '1px solid #1f2937',
                              borderRadius: 8,
                              padding: 10,
                              minHeight: 90,
                              resize: 'vertical',
                            }}
                          />
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#94a3b8' }}>
                            <span>{(d.comment || '').length}/500</span>
                            <span>Rating required</span>
                          </div>
                        </div>
                        {d.error && (
                          <div style={{ color: '#fca5a5', fontSize: 13, marginTop: 6 }}>{d.error}</div>
                        )}
                        <div className="trip-details-modal-footer" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: 0, marginTop: 10 }}>
                          <button
                            className="secondary-btn"
                            onClick={() => skipDriverReview(t.userId)}
                            style={{ borderRadius: 'var(--radius-md)' }}
                          >
                            Skip
                          </button>
                          <button
                            className="primary-btn"
                            onClick={() => submitDriverReview(t.userId)}
                            disabled={d.submitting || d.rating < 1}
                          >
                            {d.submitting ? 'Submitting…' : 'Submit Review'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverDashboard;
